import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton } from '@material-ui/core'
import Card from '../../../../../molecules/Card'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydCheckbox from '../../../../../commonDesign/SydCheckbox'
import SydButton from '../../../../../commonDesign/Button'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import Icon from '../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../constants'
import { decipherError } from '../../../../../../utils/decipherError'
import {
  COLUMN_TYPE_OPTIONS,
  sectionHeaderStyle,
  rowStyle,
  errorStyle,
  emptyStyle,
  descriptionStyle,
  getDefinition,
  normalizeKeyArray,
  useSaveDefinitionSection
} from './shared'

const autoColumnLabelStyle = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: '4px',
  fontSize: '0.6875rem',
  fontWeight: 600,
  background: 'var(--color-neutral-100)',
  color: 'var(--color-neutral-600)'
}

function getAutoColumns (definition) {
  const auto = []
  const partitionKey = definition.partitionKey || []
  const domainKey = normalizeKeyArray(definition.domainKey)

  for (const pk of partitionKey) {
    auto.push({ name: pk.name, type: pk.type, source: 'Partition Key' })
  }
  for (const dk of domainKey) {
    auto.push({ name: dk.name, type: dk.type, source: 'Domain Key' })
  }
  if (definition.changeTracking) {
    auto.push({ name: 'changeHash', type: 'varchar(32)', source: 'Change Tracking' })
    auto.push({ name: 'lastChangeDate', type: 'datetime', source: 'Change Tracking' })
  }
  auto.push({ name: 'data', type: 'json', source: 'System' })
  auto.push({ name: 'createdDate', type: 'datetime', source: 'System' })
  auto.push({ name: 'modifiedDate', type: 'datetime', source: 'System' })

  return auto
}

function ColumnRow ({ item, index, onChange, onRemove }) {
  const showLength = item.type === 'string' || item.type === 'decimal'
  const showPrecision = item.type === 'decimal'
  const showFormat = item.type === 'datetime' || item.type === 'date'

  return (
    <div style={rowStyle}>
      <Grid container spacing={1} alignItems='flex-end'>
        <Grid item xs={2}>
          <SydLabel label='Name'>
            <SydInput size='sm' value={item.name} onChange={e => onChange(index, 'name', e.target.value)} placeholder='col_name' minWidth='0' />
          </SydLabel>
        </Grid>
        <Grid item xs={2}>
          <SydLabel label='Type'>
            <SydSelect size='sm' options={COLUMN_TYPE_OPTIONS} value={item.type} onChange={e => onChange(index, 'type', e.target.value)} minWidth='0' />
          </SydLabel>
        </Grid>
        <Grid item xs={showFormat ? 2 : 3}>
          <SydLabel label='Path'>
            <SydInput size='sm' value={item.path} onChange={e => onChange(index, 'path', e.target.value)} placeholder='$.field' minWidth='0' />
          </SydLabel>
        </Grid>
        {showLength && (
          <Grid item xs={1}>
            <SydLabel label='Length'>
              <SydInput size='sm' value={item.length ?? ''} onChange={e => onChange(index, 'length', e.target.value)} minWidth='0' />
            </SydLabel>
          </Grid>
        )}
        {showPrecision && (
          <>
            <Grid item xs={1}>
              <SydLabel label='Precision'>
                <SydInput size='sm' value={item.numericPrecision ?? ''} onChange={e => onChange(index, 'numericPrecision', e.target.value)} minWidth='0' />
              </SydLabel>
            </Grid>
            <Grid item xs={1}>
              <SydLabel label='Scale'>
                <SydInput size='sm' value={item.numericScale ?? ''} onChange={e => onChange(index, 'numericScale', e.target.value)} minWidth='0' />
              </SydLabel>
            </Grid>
          </>
        )}
        {showFormat && (
          <Grid item xs={2}>
            <SydLabel label='Format'>
              <SydInput size='sm' value={item.format ?? ''} onChange={e => onChange(index, 'format', e.target.value)} placeholder='%Y-%m-%dT%H:%i:%s' minWidth='0' />
            </SydLabel>
          </Grid>
        )}
        <Grid item xs={1}>
          <SydCheckbox label='Not Null' value={item.notNull} onChange={(_, v) => onChange(index, 'notNull', v)} size='small' />
        </Grid>
        <Grid item xs={1} style={{ textAlign: 'right' }}>
          <IconButton size='small' onClick={() => onRemove(index)} title='Remove'>
            <Icon name={ICON_NAMES.close} customSize='16px' />
          </IconButton>
        </Grid>
      </Grid>
    </div>
  )
}

ColumnRow.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

function EditColumnsModal ({ open, onClose, target }) {
  const saveSection = useSaveDefinitionSection(target)
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (open) {
      const def = getDefinition(target)
      setRows((def.columns || []).map(c => ({
        name: c.name || '',
        type: c.type || 'string',
        path: c.path || '',
        length: c.length ?? null,
        numericPrecision: c.numericPrecision ?? null,
        numericScale: c.numericScale ?? null,
        format: c.format ?? null,
        notNull: c.notNull === true
      })))
      setError(null)
    }
  }, [open, target])

  const handleChange = useCallback((i, field, value) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r))
  }, [])

  const handleAdd = useCallback(() => {
    setRows(prev => [...prev, {
      name: '',
      type: 'string',
      path: '',
      length: null,
      numericPrecision: null,
      numericScale: null,
      format: null,
      notNull: false
    }])
  }, [])

  const handleRemove = useCallback((i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i))
  }, [])

  const handleSave = useCallback(async () => {
    for (const r of rows) {
      if (!r.name || !r.path) { setError('All columns must have a name and path'); return }
    }
    const columns = rows.map(c => {
      const col = { name: c.name, type: c.type, path: c.path, notNull: c.notNull }
      if (c.type === 'string' && c.length) col.length = Number(c.length)
      if (c.type === 'decimal') {
        if (c.length) col.length = Number(c.length)
        if (c.numericPrecision) col.numericPrecision = Number(c.numericPrecision)
        if (c.numericScale != null && c.numericScale !== '') col.numericScale = Number(c.numericScale)
      }
      if ((c.type === 'datetime' || c.type === 'date') && c.format) col.format = c.format
      return col
    })
    try {
      setError(null)
      setProcessing(true)
      await saveSection({ columns })
      onClose?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [rows, saveSection, onClose])

  return (
    <SydModal open={open} onClose={onClose} title='Edit Virtual Columns' size='large'>
      <div style={descriptionStyle}>
        Virtual stored columns extract values from the JSON <code>data</code> column using a path expression
        and store them as real MySQL columns. Use these for fields you need to filter, sort, or index.
        You do not need to add partition key or domain key columns here &mdash; those are included automatically as physical columns.
      </div>
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}>
        <SydButton variant='outline' size='sm' icon='add' onClick={handleAdd}>Add Column</SydButton>
      </div>
      {rows.map((item, i) => (
        <ColumnRow key={i} item={item} index={i} onChange={handleChange} onRemove={handleRemove} />
      ))}
      {!rows.length && <div style={emptyStyle}>No virtual columns defined</div>}
      <SydModalActions>
        {error ? <div style={errorStyle}>{error}</div> : null}
        <SydButton disabled={processing} variant='ghost' size='md' onClick={onClose}>Cancel</SydButton>
        <SydButton processing={processing} variant='primary' size='md' onClick={handleSave}>Save</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

EditColumnsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  target: PropTypes.object
}

export default function ColumnsCard ({ target }) {
  const [editing, setEditing] = useState(false)
  const definition = getDefinition(target)
  const columns = definition.columns || []
  const autoColumns = getAutoColumns(definition)
  const autoNames = new Set(autoColumns.map(c => c.name))
  const totalCount = autoColumns.length + columns.length

  return (
    <Card>
      <div style={sectionHeaderStyle}>
        <h3>Columns ({totalCount})</h3>
        <IconButton size='small' onClick={() => setEditing(true)} title='Edit Virtual Columns'>
          <Icon name={ICON_NAMES.edit} customSize='18px' />
        </IconButton>
      </div>
      <table className='opal-table'>
        <thead>
          <tr><th>Name</th><th>Type</th><th>Source</th><th>Not Null</th></tr>
        </thead>
        <tbody>
          {autoColumns.map((c, i) => (
            <tr key={`auto-${i}`} style={{ opacity: 0.7 }}>
              <td><code>{c.name}</code></td>
              <td>{c.type}</td>
              <td><span style={autoColumnLabelStyle}>{c.source}</span></td>
              <td>Yes</td>
            </tr>
          ))}
          {columns.length > 0 && (
            <tr>
              <td colSpan={4} style={{ padding: '4px 8px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', background: 'var(--color-neutral-50)' }}>
                Virtual Stored Columns
              </td>
            </tr>
          )}
          {columns.map((c, i) => (
            <tr key={`user-${i}`}>
              <td>
                <code>{c.name}</code>
                {autoNames.has(c.name) && (
                  <span style={{ ...autoColumnLabelStyle, background: 'var(--color-warning-100)', color: 'var(--color-warning-700)', marginLeft: '6px' }}>
                    redundant
                  </span>
                )}
              </td>
              <td>{c.type}{c.length ? `(${c.length})` : ''}{c.numericPrecision ? `(${c.numericPrecision},${c.numericScale ?? 0})` : ''}</td>
              <td><code>{c.path}</code></td>
              <td>{c.notNull ? 'Yes' : 'No'}</td>
            </tr>
          ))}
          {!columns.length && (
            <tr>
              <td colSpan={4} style={{ fontStyle: 'italic', color: 'var(--color-neutral-600)' }}>
                No additional virtual columns defined
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <EditColumnsModal open={editing} onClose={() => setEditing(false)} target={target} />
    </Card>
  )
}

ColumnsCard.propTypes = { target: PropTypes.object.isRequired }
